import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createCustomerSchema, type CreateCustomerInput } from '@wrapforce/shared';
import { Link, useSearchParams } from 'react-router';
import { Search, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useCustomers, useCreateCustomer } from './api';
import { useDebounce } from '@/lib/useDebounce';
import { useLocaleFormatters } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input, Label, FieldError } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';

export function CustomersPage() {
  const { t } = useTranslation('customers');
  const { formatMoney } = useLocaleFormatters();
  const { membership } = useActiveMembership();
  const workspaceId = membership?.workspaceId ?? null;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [addOpen, setAddOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // The topbar's quick-add button lands here with ?new=1; the dialog derives
  // its open state from the param so it survives the navigation, and closing
  // strips the param again.
  const addDialogOpen = addOpen || searchParams.get('new') !== null;
  function setAddDialogOpen(open: boolean) {
    setAddOpen(open);
    if (!open && searchParams.get('new') !== null) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('new');
        return next;
      });
    }
  }

  const { data, isLoading } = useCustomers(workspaceId, { query: debouncedSearch.length >= 2 ? debouncedSearch : undefined });

  return (
    <div className="flex flex-col gap-4 p-6">
      <div className="flex items-center gap-3">
        <div className="flex w-full items-center gap-2 border border-border bg-surface px-3.5 py-2 sm:w-72">
          <Search size={14} className="text-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('search.placeholder')}
            className="w-full bg-transparent text-sm outline-none"
          />
        </div>
        <Button size="sm" className="ml-auto gap-1.5" onClick={() => setAddOpen(true)}>
          <Plus size={14} /> {t('actions.newCustomer')}
        </Button>
      </div>

      {/* Mobile: tappable card rows. Tables scroll poorly on phones, so the
          same rows render as a stacked card list below md. */}
      <div className="flex flex-col gap-2 md:hidden">
        {(data?.items ?? []).map((c) => (
          <Link
            key={c.id}
            to={`/customers/${c.id}`}
            className="flex flex-col gap-1.5 rounded-[var(--radius-card)] border border-border bg-surface p-3.5 text-left"
          >
            <span className="text-[15px] font-semibold text-ink">{c.fullName}</span>
            {c.phone || c.email ? (
              <div className="truncate text-xs text-muted">{c.phone || c.email}</div>
            ) : null}
            <div className="mt-1 flex items-center justify-between border-t border-border pt-1.5 text-xs text-muted">
              <span>{t('table.headers.vehicles')}: {c.vehicleCount}</span>
              <span className="font-mono tabular-nums">{formatMoney(c.totalSpentCents)}</span>
            </div>
          </Link>
        ))}
        {!isLoading && (data?.items.length ?? 0) === 0 ? (
          <div className="rounded-[var(--radius-card)] border border-border bg-surface px-4 py-10 text-center text-sm text-muted">{t('table.empty')}</div>
        ) : null}
      </div>

      <div className="hidden overflow-hidden rounded-[var(--radius-card)] border border-border bg-surface md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-surface-2 text-left text-xs font-bold uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-2.5">{t('table.headers.name')}</th>
              <th className="px-4 py-2.5">{t('table.headers.contact')}</th>
              <th className="px-4 py-2.5">{t('table.headers.vehicles')}</th>
              <th className="px-4 py-2.5">{t('table.headers.totalSpent')}</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((c) => (
              <tr key={c.id} className="border-b border-border last:border-0 hover:bg-surface-2">
                <td className="px-4 py-3">
                  <Link to={`/customers/${c.id}`} className="font-semibold text-ink hover:text-accent-ink">
                    {c.fullName}
                  </Link>
                </td>
                <td className="px-4 py-3 text-muted">{c.phone || c.email || '-'}</td>
                <td className="px-4 py-3 tabular-nums">{c.vehicleCount}</td>
                <td className="px-4 py-3 font-mono tabular-nums">{formatMoney(c.totalSpentCents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && (data?.items.length ?? 0) === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted">{t('table.empty')}</div>
        ) : null}
      </div>

      {workspaceId ? <AddCustomerDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} workspaceId={workspaceId} /> : null}
    </div>
  );
}

function AddCustomerDialog({ open, onOpenChange, workspaceId }: { open: boolean; onOpenChange: (v: boolean) => void; workspaceId: string }) {
  const { t } = useTranslation('customers');
  const createCustomer = useCreateCustomer(workspaceId);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateCustomerInput>({ resolver: zodResolver(createCustomerSchema) });

  async function onSubmit(values: CreateCustomerInput) {
    await createCustomer.mutateAsync(values);
    toast.success(t('toast.customerAdded'));
    reset();
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={t('addDialog.title')}>
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="fullName">{t('addDialog.labels.fullName')}</Label>
          <Input id="fullName" {...register('fullName')} />
          <FieldError>{errors.fullName?.message}</FieldError>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="phone">{t('addDialog.labels.phone')}</Label>
          <Input id="phone" {...register('phone')} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="email">{t('addDialog.labels.email')}</Label>
          <Input id="email" type="email" {...register('email', { setValueAs: (v) => v || undefined })} />
          <FieldError>{errors.email?.message}</FieldError>
        </div>
        <Button type="submit" loading={createCustomer.isPending} className="mt-1 w-full">
          {t('actions.addCustomer')}
        </Button>
      </form>
    </Dialog>
  );
}
